import React, { useState } from 'react';
import {
  relativeDay, fmtDay, fmtDuration, fmtTime, addDays, priorityMeta, categoryMeta,
  planHours, hourSlot, planDay, planBacklog, planCapacity, sortTasks,
} from '../myDayUtils';

/**
 * Plan the next day.
 *
 * The shape follows the planning ritual the guided planners settled on —
 * pick what you'll work on, defer the rest, then put it in order — but the
 * interaction is tap, not drag. Dragging a card onto an hour is a desktop
 * gesture; this gets used one-handed on a phone in a truck, so pulling work in
 * is a tap and giving it a time is a tap on an hour chip.
 *
 * The capacity bar measures against the hours actually left before the owner
 * says they want to be finished, which is what makes over-committing land:
 * "seven hours of work before a six-hour finish" is a sentence you can act on.
 */
export default function PlanView({
  tasks, cats, today, date, settings, dayMeta,
  onPick, onSetDate, onSchedule, onOpen, onToggle, onDayMeta, onSettings, onQuickAdd,
}) {
  const [slotFor, setSlotFor] = useState(null);   // task awaiting an hour
  const [openBucket, setOpenBucket] = useState('carrying');
  const [draft, setDraft] = useState('');

  const hours = planHours(settings);
  const day = planDay(tasks, date);
  const backlog = planBacklog(tasks, date, today);
  const dayTasks = tasks.filter(t => !t.archived && !t.done && t.date === date);
  const load = planCapacity(dayTasks, settings);

  const buckets = [
    { id: 'carrying', label: 'Still chasing you', hint: 'Open today or earlier', items: backlog.carrying },
    { id: 'later', label: 'Put off until later', hint: 'Already dated past this day', items: backlog.later },
    { id: 'shelved', label: 'Shelved', hint: 'Taken off the list — bring one back if it is time', items: backlog.shelved },
  ];

  const submitDraft = e => {
    e.preventDefault();
    if (!draft.trim()) return;
    onQuickAdd(draft, date);
    setDraft('');
  };

  const Pill = ({ task, onClick, label, title }) => {
    const pri = priorityMeta(task.priority);
    const cat = task.category ? categoryMeta(task.category, cats) : null;
    return (
      <li className="md-plan__pill">
        <button className="md-plan__pillmain" onClick={() => onOpen(task)} title="Open details">
          <span className="md-flag" style={{ color: pri.color, borderColor: pri.color }}>{pri.short}</span>
          <span className="md-plan__pilltitle">{task.title}</span>
          {task.estimateMin ? <span className="md-meta">{fmtDuration(task.estimateMin)}</span> : null}
          {cat && <span className="md-meta md-meta--cat" style={{ color: cat.color, borderColor: `${cat.color}66` }}>{cat.icon}</span>}
          {(task.carryCount || 0) > 0 && <span className="md-meta md-meta--carry">↪ ×{task.carryCount}</span>}
        </button>
        <button className="md-btn md-btn--sm" onClick={onClick} title={title}>{label}</button>
      </li>
    );
  };

  return (
    <div className="md-plan">
      <p className="md-lead">
        Decide tomorrow tonight, and the morning starts already knowing what it is for.
        Choose what you'll actually do, then give the parts that need a fixed time an hour.
      </p>

      {/* ── The day being planned ──────────────────────────────────────── */}
      <header className="md-plan__head">
        <div className="md-plan__when">
          <button className="md-iconbtn" onClick={() => onSetDate(addDays(date, -1))} aria-label="Previous day">‹</button>
          <span className="md__daylabel">
            <strong>{relativeDay(date, today)}</strong>
            <em>{fmtDay(date, { weekday: 'long', month: 'long', day: 'numeric' })}</em>
          </span>
          <button className="md-iconbtn" onClick={() => onSetDate(addDays(date, 1))} aria-label="Next day">›</button>
          {date !== addDays(today, 1) && (
            <button className="md-btn md-btn--sm" onClick={() => onSetDate(addDays(today, 1))}>Plan tomorrow</button>
          )}
        </div>
        <label className="md-plan__finish">
          <span className="md-field__lbl">Want to be finished by</span>
          <select className="md-input md-input--sm" value={settings.finishByHour}
            onChange={e => onSettings({ finishByHour: Number(e.target.value) })}>
            {hours.slice(1).concat([hours[hours.length - 1] + 1]).map(h => (
              <option key={h} value={h}>{fmtTime(hourSlot(h))}</option>
            ))}
          </select>
        </label>
      </header>

      <label className="md__intention">
        <span className="md__intention-lbl">🎯 What would make {relativeDay(date, today).toLowerCase()} a win?</span>
        <input className="md-input" value={dayMeta.intention || ''}
          placeholder="One sentence — the thing you'd be glad you did."
          onChange={e => onDayMeta({ intention: e.target.value })} />
      </label>

      <div className={`md-load md-load--${load.state}`}>
        <span className="md-load__fill" style={{ width: `${Math.min(100, load.pct)}%` }} />
        <span className="md-load__txt">
          {day.total === 0
            ? `Nothing planned yet · ${fmtDuration(load.capacityMin)} of room before ${fmtTime(hourSlot(settings.finishByHour))}`
            : `${fmtDuration(load.plannedMin)} planned into ${fmtDuration(load.capacityMin)}`}
          {load.state === 'over' && ` — ${fmtDuration(load.overMin)} too much, move something`}
          {load.state === 'tight' && ' — a full day, leave room for the truck'}
          {load.untimedCount > 0 && ` · ${load.untimedCount} with no estimate`}
        </span>
      </div>

      <div className="md-plan__grid">
        {/* ── Timeline ─────────────────────────────────────────────────── */}
        <section className="md-card md-plan__day">
          <header className="md-card__head">
            <div>
              <h3 className="md-card__title">The day</h3>
              <p className="md-card__sub">
                {day.total === 0 ? 'Empty — pull something in.' : `${day.timed.length} at a set time · ${day.anytime.length} to fit in`}
              </p>
            </div>
          </header>

          <ol className="md-plan__timeline">
            {hours.map(h => {
              const inHour = day.byHour.get(h) || [];
              return (
                <li key={h} className={`md-plan__hour${inHour.length ? ' md-plan__hour--busy' : ''}`}>
                  <span className="md-plan__hourlbl">{fmtTime(hourSlot(h))}</span>
                  <div className="md-plan__slot">
                    {inHour.map(t => (
                      <div key={t.id} className="md-plan__block">
                        <button className="md-check md-check--sm" onClick={() => onToggle(t)} aria-label={`Mark ${t.title} done`}>
                          <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="3.5">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </button>
                        <button className="md-plan__blockmain" onClick={() => onOpen(t)}>
                          <span className="md-plan__blocktime">{fmtTime(t.startTime)}</span>
                          <span className="md-plan__pilltitle">{t.title}</span>
                          {t.estimateMin ? <span className="md-meta">{fmtDuration(t.estimateMin)}</span> : null}
                        </button>
                        <button className="md-iconbtn" onClick={() => onSchedule(t, null)}
                          title="Take it off the clock"
                          aria-label={`Take "${t.title}" off the clock`}>✕</button>
                      </div>
                    ))}
                    {inHour.length === 0 && (
                      <button className="md-plan__empty" onClick={() => onQuickAdd('', date, hourSlot(h))}>
                        + add at {fmtTime(hourSlot(h))}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="md-plan__anytime">
            <h4 className="md-plan__subhead">Anytime that day — {day.anytime.length}</h4>
            {day.anytime.length === 0 ? (
              <p className="md-empty md-empty--sm">Nothing loose. Everything planned has a time.</p>
            ) : (
              <ul className="md-plan__pills">
                {day.anytime.map(t => (
                  <Pill key={t.id} task={t} label="🕐 Give it a time" title="Pick an hour"
                    onClick={() => setSlotFor(slotFor?.id === t.id ? null : t)} />
                ))}
              </ul>
            )}
            {slotFor && (
              <div className="md-plan__slots">
                <span className="md-field__lbl">Start “{slotFor.title}” at</span>
                <div className="md-quickdates">
                  {hours.map(h => (
                    <button key={h} className="md-chip md-chip--pick"
                      onClick={() => { onSchedule(slotFor, hourSlot(h)); setSlotFor(null); }}>
                      {fmtTime(hourSlot(h))}
                    </button>
                  ))}
                  <button className="md-chip" onClick={() => setSlotFor(null)}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          <form className="md-sub__add md-plan__add" onSubmit={submitDraft}>
            <input className="md-input md-input--sm" value={draft}
              placeholder={`Add straight to ${relativeDay(date, today).toLowerCase()} — plain English works`}
              onChange={e => setDraft(e.target.value)} />
            <button className="md-btn md-btn--primary md-btn--sm" type="submit" disabled={!draft.trim()}>Add</button>
          </form>
        </section>

        {/* ── What can go in ───────────────────────────────────────────── */}
        <section className="md-card md-plan__pool">
          <header className="md-card__head">
            <div>
              <h3 className="md-card__title">Pull into the day</h3>
              <p className="md-card__sub">Tap an activity to move it onto {relativeDay(date, today).toLowerCase()}.</p>
            </div>
          </header>

          <div className="md-seg md-seg--wrap">
            {buckets.map(b => (
              <button key={b.id} className={`md-seg__btn${openBucket === b.id ? ' md-seg__btn--on' : ''}`}
                onClick={() => setOpenBucket(b.id)}>
                {b.label} <span className="md-group__count">{b.items.length}</span>
              </button>
            ))}
          </div>

          {buckets.filter(b => b.id === openBucket).map(b => (
            <div key={b.id} className="md-plan__bucket">
              <p className="md-card__sub">{b.hint}</p>
              {b.items.length === 0 ? (
                <p className="md-empty md-empty--sm">Nothing here — which is a good sign.</p>
              ) : (
                <ul className="md-plan__pills">
                  {sortTasks(b.items).map(t => (
                    <Pill key={t.id} task={t} label="→ Move in" title={`Move to ${relativeDay(date, today).toLowerCase()}`}
                      onClick={() => onPick(t, date)} />
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
